import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useSetDocumentTitle, useTrans } from "../../../hooks";
import { Icon, Loader } from "../../../../components/shared-components";
import BrowseStoryItem, {
  BrowseStoryItemProps
} from "../../../../components/views/story-item/BrowseStoryItem";
import { fetchPremiumPicks } from "../reducers/premium-picks-catalog-reducer";

const Tabs = {
  THIS_MONTH: "thismonthpicks",
  NEXT_MONTH: "nextmonthpicks"
};

const PremiumPicksCatalog = ({
  className,
  currentPicks,
  upcomingPicks,
  isLoading,
  loadError,
  fetchPremiumPicks
}) => {
  const { trans } = useTrans();
  const [activeTab, setActiveTab] = useState(Tabs.THIS_MONTH);

  useEffect(
    () => {
      fetchPremiumPicks();
    },
    [fetchPremiumPicks]
  );

  const getStories = useCallback(
    () => {
      switch (activeTab) {
        case Tabs.THIS_MONTH:
          return currentPicks?.stories;
        case Tabs.NEXT_MONTH:
          return upcomingPicks?.stories;
        default:
          return [];
      }
    },
    [activeTab, currentPicks, upcomingPicks]
  );

  useSetDocumentTitle(trans("Catalog") + " - " + trans("Premium Picks"));

  return (
    <div className={classNames(className, "premium-picks-catalog")}>
      <div className="catalog-header">
        <div className="container">
          <h1 className="heading">
            <Icon
              name="zap"
              size="20"
              className="zap-icon"
              color="ds-base-4-accent"
              fill
            />
            {trans("Premium Picks")}
          </h1>
          <div className="description">
            {trans(
              "Each month, access 5 Paid Stories with any Premium or Premium+ plan."
            )}{" "}
            <a href="/" className="learn-more-link on-navigate">
              {trans("Learn more")}
            </a>
          </div>
          <div className="access-expires-pill">
            <Icon
              name="timeout"
              size="12"
              className="timeout"
              color="ds-neutral-100"
              strokeWidth="1"
              fill
            />
            {trans("Access expires in")} {"28d 5h"}
          </div>
        </div>
      </div>

      <div className="container">
        <div className="catalog-navigation">
          <button
            className={classNames({ active: activeTab === Tabs.THIS_MONTH })}
            onClick={() => setActiveTab(Tabs.THIS_MONTH)}
          >
            {trans("This month")}
          </button>
          <button
            className={classNames({ active: activeTab === Tabs.NEXT_MONTH })}
            onClick={() => setActiveTab(Tabs.NEXT_MONTH)}
          >
            {trans("Next month")}
          </button>
        </div>

        <div className="catalog-stories">
          {isLoading ? (
            <div className="loading-spinner">
              <Loader />
            </div>
          ) : null}
          {!isLoading
            ? getStories().map(item => (
                <div className="browse-story-item" key={`story-${item.id}`}>
                  <BrowseStoryItem {...item} {...{ trans, app: window?.app }} />
                </div>
              ))
            : null}
          {loadError ? trans("Failed to load Premium Picks") : null}
        </div>
      </div>
    </div>
  );
};

const readingListProps = {
  id: PropTypes.number,
  name: PropTypes.string,
  stories: PropTypes.arrayOf(PropTypes.shape(BrowseStoryItemProps)),
  total: PropTypes.number
};

PremiumPicksCatalog.propTypes = {
  className: PropTypes.string,
  currentPicks: PropTypes.shape(readingListProps),
  upcomingPicks: PropTypes.shape(readingListProps),
  isLoading: PropTypes.bool,
  loadError: PropTypes.string,
  fetchPremiumPicks: PropTypes.func
};

export default connect(
  state => ({
    currentPicks: state.premiumPicksCatalog.currentPicks,
    upcomingPicks: state.premiumPicksCatalog.upcomingPicks,
    isLoading: state.premiumPicksCatalog.isLoading,
    loadError: state.premiumPicksCatalog.loadError
  }),
  { fetchPremiumPicks }
)(PremiumPicksCatalog);
